import {Injectable} from '@angular/core';
import {ApiService} from "./api.service";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {BehaviorSubject, Observable} from "rxjs";
import {Pagination} from "@core/shared/models/pagination";
import {ShortSociety, Society} from '@core/shared/models/society';
import {map} from "rxjs/operators";
import {AllPaginateItemsAPI, DEFAULT_ALL_PAGINATE_ITEMS_API_LIMIT} from "@core/shared/models/all-paginate-items-api";
import {ShortItem} from "@core/shared/models/short-item";
import {SocietyValidationAccount} from "@core/shared/models/society-validation-account";
import {AdditionalHeader} from "@core/shared/models/http-client";

@Injectable({
    providedIn: 'root'
})
export class SocietyService {

    public currentUserSociety: BehaviorSubject<Society> = new BehaviorSubject(null);

    public validationAccountStatusChecked$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    public currentSocietyValidationAccount$: BehaviorSubject<SocietyValidationAccount> = new BehaviorSubject({
        isValid: false,
        redirectUrl: null
    });

    constructor(
        private _apiService: ApiService,
        private _httpClient: HttpClient,
    ) {}

    public getItemsAPI(params?: string[], limit?: number): Observable<Society[]> {

        return new AllPaginateItemsAPI<Society>(this.getPaginationItemsAPI.bind(this), params, limit || DEFAULT_ALL_PAGINATE_ITEMS_API_LIMIT).request$;
    }

    public getShortItemsAPI(params?: string[]): Observable<ShortSociety[]> {

        return new AllPaginateItemsAPI<ShortSociety>(this.getPaginationShortItemsAPI.bind(this), params).request$;
    }

    public getOfferCreatorsShortItemsAPI(params?: string[]): Observable<ShortSociety[]> {

        return new AllPaginateItemsAPI<ShortSociety>(this.getPaginationOfferCreatorsShortItemsAPI.bind(this), params).request$;
    }

    public getPaginationOfferCreatorsShortItemsAPI(params?: string[]): Observable<Pagination<ShortSociety>> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        let url: string = `${this._apiService.getApiUrl(false, true)}/societies/offerCreators/shorts`;

        params = params || [];

        if (params.length > 0) {

            url = `${url}?${params.join('&')}`;
        }

        return this._httpClient.get<Pagination<ShortItem<ShortSociety>>>(url, {
            headers: headers
        }).pipe(map((pagination: Pagination<ShortItem<ShortSociety>>): Pagination<ShortSociety> => {

            return Object.assign(pagination, {
                items: pagination.items.map((item: ShortItem<ShortSociety>): ShortSociety => {

                    return Object.assign(item.shortValues, {
                        id: parseInt(item.shortIdentifier)
                    }) as ShortSociety;
                })
            });
        }));
    }

    public getPaginationChannelShortItemsAPI(params?: string[]): Observable<Pagination<Society>> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        let url: string = `${this._apiService.getApiUrl(false, true)}/societies/channels/shorts`;

        params = params || [];

        if (params.length > 0) {

            url = `${url}?${params.join('&')}`;
        }

        return this._httpClient.get<Pagination<ShortItem<Society>>>(url, {
            headers: headers
        }).pipe(map((pagination: Pagination<ShortItem<Society>>): Pagination<Society> => {

            return Object.assign(pagination, {
                items: pagination.items.map((item: ShortItem<ShortSociety>): Society => {

                    return Object.assign(item.shortValues, {
                        id: parseInt(item.shortIdentifier)
                    }) as Society;
                })
            });
        }));
    }

    public getOfferDistributorsItemsAPI(params?: string[]): Observable<Society[]> {

        return new AllPaginateItemsAPI<Society>(this.getPaginationOfferDistributorsItemsAPI.bind(this), params).request$;
    }

    public getPaginationOfferDistributorsItemsAPI(params?: string[]): Observable<Pagination<Society>> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        let url: string = `${this._apiService.getApiUrl(false, true)}/societies/offerDistributors`;

        params = params || [];

        if (params.length > 0) {

            url = `${url}?${params.join('&')}`;
        }

        return this._httpClient.get<Pagination<Society>>(url, {
            headers: headers
        });
    }

    public getProvidersItemsAPI(params?: string[]): Observable<Society[]> {

        return new AllPaginateItemsAPI<Society>(this.getPaginationProvidersItemsAPI.bind(this), params).request$;
    }

    public getPaginationProvidersItemsAPI(params?: string[]): Observable<Pagination<Society>> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        let url: string = `${this._apiService.getApiUrl(false, true)}/societies/providers`;

        params = params || [];

        if (params.length > 0) {

            url = `${url}?${params.join('&')}`;
        }

        return this._httpClient.get<Pagination<Society>>(url, {
            headers: headers
        });
    }

    public getPaginationItemsAPI(params?: string[]): Observable<Pagination<Society>> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        let url: string = `${this._apiService.getApiUrl(false, true)}/societies`;

        params = params || [];

        if (params.length > 0) {

            url = `${url}?${params.join('&')}`;
        }

        return this._httpClient.get<Pagination<Society>>(url, {
            headers: headers
        });
    }

    public getPaginationShortItemsAPI(params?: string[]): Observable<Pagination<ShortSociety>> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        let url: string = `${this._apiService.getApiUrl(false, true)}/societies/shorts`;

        params = params || [];

        if (params.length > 0) {

            url = `${url}?${params.join('&')}`;
        }

        return this._httpClient.get<Pagination<ShortItem<ShortSociety>>>(url, {
            headers: headers
        }).pipe(map((pagination: Pagination<ShortItem<ShortSociety>>): Pagination<ShortSociety> => {

            return Object.assign(pagination, {
                items: pagination.items.map((item: ShortItem<ShortSociety>): ShortSociety => {

                    return Object.assign(item.shortValues, {
                        id: parseInt(item.shortIdentifier)
                    }) as ShortSociety;
                })
            });
        }));
    }

    public getPaginationArticleShortItemsAPI(params?: string[]): Observable<Pagination<ShortSociety>> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        let url: string = `${this._apiService.getApiUrl(false, true)}/articles/societies/shorts`;

        params = params || [];

        if (params.length > 0) {

            url = `${url}?${params.join('&')}`;
        }

        return this._httpClient.get<Pagination<ShortItem<ShortSociety>>>(url, {
            headers: headers
        }).pipe(map((pagination: Pagination<ShortItem<ShortSociety>>): Pagination<ShortSociety> => {

            return Object.assign(pagination, {
                items: pagination.items.map((item: ShortItem<ShortSociety>): ShortSociety => {

                    return Object.assign(item.shortValues, {
                        id: parseInt(item.shortIdentifier)
                    }) as ShortSociety;
                })
            });
        }));
    }

    public getArticleShortItemsAPI(params?: string[]): Observable<ShortSociety[]> {

        return new AllPaginateItemsAPI<ShortSociety>(this.getPaginationArticleShortItemsAPI.bind(this), params).request$;
    }

    public getItemAPI(id: number, params?: string[]): Observable<Society> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        let url: string = `${this._apiService.getApiUrl(false, true)}/societies/${id}`;

        params = params || [];

        if (params.length > 0) {

            url = `${url}?${params.join('&')}`;
        }

        return this._httpClient.get<Society>(url, {
            headers: headers
        });
    }

    public getItemSelfAPI(): Observable<Society> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        return this._httpClient.get<Society>(`${this._apiService.getApiUrl(false, true)}/users/self/society`, {
            headers: headers
        });
    }

    public createItemAPI(data: object): Observable<Society> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        return this._httpClient.post<Society>(`${this._apiService.getApiUrl(false, true)}/societies`, data, {
            headers: headers
        });
    }

    public createReferenceShareAPI(data: object): Observable<any> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        return this._httpClient.post<Society>(`${this._apiService.getApiUrl(false, true)}/societies/reference/share`, data, {
            headers: headers
        });
    }

    public deleteItemAPI(id: number): Observable<void> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        return this._httpClient.delete<void>(`${this._apiService.getApiUrl(false, true)}/societies/${id}`,{
            headers: headers
        });
    }

    public updateItemAPI(id: number, data: object): Observable<Society> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        return this._httpClient.patch<Society>(`${this._apiService.getApiUrl(false, true)}/societies/${id}`, data, {
            headers: headers
        });
    }

    public getTermAndConditionsFromOffer(id: number, locale: string): Observable<number> {

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');
        return this._httpClient.get<number>(`${this._apiService.getApiUrl(false, true)}/societies/offer/${id}/${locale}/terms-and-conditions`, {
            headers: headers
        });
    }

    public exportItemsAPI(): Observable<Blob> {

        return this._httpClient.get(`${this._apiService.getApiUrl(false, true)}/societies/export`, {
            responseType: 'blob'
        });

    }

    public cancelDeleteItemAPI(id: number): Observable<void>{

        const headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        return this._httpClient.post<void>(`${this._apiService.getApiUrl(false, true)}/societies/${id}/delete/cancel`,{
            headers: headers
        });
    }

    public getItemValidationAccountAPI(id: number, additionalHeaders: AdditionalHeader[] = []): Observable<SocietyValidationAccount> {

        let headers: HttpHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Accept', 'application/json');

        additionalHeaders?.forEach(header => headers = headers.append(header.name, header.value.toString()));

        return this._httpClient.get<SocietyValidationAccount>(`${this._apiService.getApiUrl(false, true)}/societies/${id}/validate/account`, {
            headers: headers
        });
    }

    public resetValidationAccountStatus(): void {

        this.validationAccountStatusChecked$.next(false);
    }

    get validationAccountStatusChecked(): boolean {

        return this.validationAccountStatusChecked$.getValue();
    }
}
